import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../context/AppContext';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  DocumentDuplicateIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import Drawer from './Drawer';

const CATEGORY_LABELS = {
  contract: 'Contract',
  invoice: 'Invoice',
  receipt: 'Receipt',
  letter: 'Letter',
  report: 'Report',
  work_order: 'Work Order',
  other: 'Other'
};

const ENTITY_TYPE_LABELS = {
  sale: 'Sale',
  contact: 'Contact',
  plot: 'Plot',
  work_order: 'Work Order',
  general: 'General'
};

const DocumentTemplates = () => {
  const { token } = useContext(AppContext);
  const navigate = useNavigate();

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [entityTypeFilter, setEntityTypeFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Preview drawer
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (categoryFilter) params.append('category', categoryFilter);
      if (entityTypeFilter) params.append('entityType', entityTypeFilter);

      const response = await fetch(`/api/document-templates?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      } else {
        const err = await response.json();
        setError(err.error || 'Failed to fetch templates');
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError('Failed to fetch templates');
    } finally {
      setLoading(false);
    }
  }, [token, categoryFilter, entityTypeFilter]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Filter templates by search term
  const filteredTemplates = templates.filter(t => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      t.name.toLowerCase().includes(search) ||
      t.templateKey.toLowerCase().includes(search) ||
      (t.description && t.description.toLowerCase().includes(search))
    );
  });

  const handleDelete = async (template) => {
    try {
      const response = await fetch(`/api/document-templates/${template.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        setTemplates(prev => prev.filter(t => t.id !== template.id));
        setDeleteConfirm(null);
      } else {
        const err = await response.json();
        setError(err.error || 'Failed to delete template');
      }
    } catch (err) {
      console.error('Error deleting template:', err);
      setError('Failed to delete template');
    }
  };

  const handleToggleActive = async (template) => {
    try {
      const response = await fetch(`/api/document-templates/${template.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ isActive: !template.isActive })
      });

      if (response.ok) {
        const updated = await response.json();
        setTemplates(prev => prev.map(t => t.id === updated.id ? updated : t));
      }
    } catch (err) {
      console.error('Error toggling template:', err);
    }
  };

  const handlePreview = async (template) => {
    setPreviewTemplate(template);
    setPreviewLoading(true);
    setPreviewHtml('');

    try {
      const response = await fetch(`/api/document-templates/${template.id}/preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({})
      });

      if (response.ok) {
        const data = await response.json();
        setPreviewHtml(data.html);
      } else {
        setPreviewHtml('<p style="color: red;">Failed to generate preview</p>');
      }
    } catch (err) {
      console.error('Error previewing template:', err);
      setPreviewHtml('<p style="color: red;">Failed to generate preview</p>');
    } finally {
      setPreviewLoading(false);
    }
  };

  const clearFilters = () => {
    setCategoryFilter('');
    setEntityTypeFilter('');
    setSearchTerm('');
  };

  const hasActiveFilters = categoryFilter || entityTypeFilter || searchTerm;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Document Templates</h1>
          <p className="mt-1 text-sm text-gray-500">
            Create and manage document templates for contracts, invoices, and more.
          </p>
        </div>
        <button
          onClick={() => navigate('/documents/templates/new')}
          className="inline-flex items-center gap-2 rounded-md bg-brand-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-green-700"
        >
          <PlusIcon className="h-5 w-5" />
          New Template
        </button>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search templates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-brand-green-500 focus:border-brand-green-500"
            />
          </div>

          {/* Filter toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`inline-flex items-center gap-2 px-4 py-2 border rounded-md ${
              hasActiveFilters ? 'border-brand-green-500 text-brand-green-700 bg-brand-green-50' : 'border-gray-300 text-gray-700'
            }`}
          >
            <FunnelIcon className="h-5 w-5" />
            Filters
            {hasActiveFilters && (
              <span className="bg-brand-green-600 text-white text-xs px-2 py-0.5 rounded-full">
                {[categoryFilter, entityTypeFilter].filter(Boolean).length}
              </span>
            )}
          </button>
        </div>

        {/* Filter options */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 flex flex-wrap gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="">All Categories</option>
                {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Entity Type</label>
              <select
                value={entityTypeFilter}
                onChange={(e) => setEntityTypeFilter(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="">All Types</option>
                {Object.entries(ENTITY_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="self-end text-sm text-brand-green-600 hover:text-brand-green-700"
              >
                Clear filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
          <button onClick={() => setError('')} className="float-right">×</button>
        </div>
      )}

      {/* Templates list */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading templates...</div>
        ) : filteredTemplates.length === 0 ? (
          <div className="p-8 text-center">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900">No templates</h3>
            <p className="mt-1 text-sm text-gray-500">
              {hasActiveFilters
                ? 'No templates match your filters.'
                : 'Get started by creating a new document template.'}
            </p>
            {!hasActiveFilters && (
              <button
                onClick={() => navigate('/documents/templates/new')}
                className="mt-4 inline-flex items-center gap-2 text-sm text-brand-green-600 hover:text-brand-green-700"
              >
                <PlusIcon className="h-5 w-5" />
                Create your first template
              </button>
            )}
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Template
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Entity Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Version
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredTemplates.map((template) => (
                <tr
                  key={template.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/documents/templates/${template.id}`)}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <DocumentTextIcon className="h-8 w-8 text-gray-400 mr-3" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">{template.name}</div>
                        <div className="text-sm text-gray-500">{template.templateKey}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {CATEGORY_LABELS[template.category] || template.category}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {ENTITY_TYPE_LABELS[template.entityType] || template.entityType}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleToggleActive(template); }}
                      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        template.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {template.isActive ? (
                        <>
                          <CheckCircleIcon className="h-4 w-4" />
                          Active
                        </>
                      ) : (
                        <>
                          <XCircleIcon className="h-4 w-4" />
                          Inactive
                        </>
                      )}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className="inline-flex items-center gap-1">
                      <ClockIcon className="h-4 w-4" />
                      v{template.version}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => handlePreview(template)}
                        className="text-gray-400 hover:text-gray-600"
                        title="Preview"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => navigate(`/documents/templates/${template.id}`)}
                        className="text-brand-green-600 hover:text-brand-green-700"
                        title="Edit"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => navigate(`/documents/templates/new?duplicate=${template.id}`)}
                        className="text-gray-400 hover:text-gray-600"
                        title="Duplicate"
                      >
                        <DocumentDuplicateIcon className="h-5 w-5" />
                      </button>
                      {!template.isSystem && (
                        <button
                          onClick={() => setDeleteConfirm(template)}
                          className="text-red-400 hover:text-red-600"
                          title="Delete"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Template</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete "{deleteConfirm.name}"? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview drawer */}
      <Drawer
        title={previewTemplate ? `Preview: ${previewTemplate.name}` : 'Preview'}
        open={!!previewTemplate}
        setOpen={() => setPreviewTemplate(null)}
      >
        <div className="h-full overflow-auto">
          {previewLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-green-600"></div>
            </div>
          ) : (
            <iframe
              srcDoc={previewHtml}
              className="w-full h-full min-h-[600px] border-0"
              title="Document Preview"
            />
          )}
        </div>
      </Drawer>
    </div>
  );
};

export default DocumentTemplates;
